import React, { useCallback } from 'react';
import { MANAGE_ORDERS_LABELS_ID } from '../../../config/constants';
import { Box, Typography, Loader } from '@lucid/lego';
import { useStore } from '../../../store/store';
import { useLabels } from '../../../hooks/useLabels';
import { useLocaleNavigate } from '../../../hooks/useLocaleNavigate';
import { convertToContentfulLocale } from '../../../utils/tools';
import Table from './Table/Table';
import { useOrdersTable } from './hooks/useOrdersTable';
import styles from './ManageOrders.module.scss';

/**
 * Manage Orders page component - displays and manages all orders
 * @component
 * @returns {JSX.Element} Manage Orders page
 */
const ManageOrders: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const navigate = useLocaleNavigate();

  // Handle order selection (navigates to details page)
  const handleOrderSelect = useCallback(
    (orderIdentifier: string | null, orderNumber?: string | null) => {
      if (orderIdentifier) {
        navigate(`/order-hub/manage-orders/${orderIdentifier}`, {
          state: { orderNumber },
        });
      }
    },
    [navigate]
  );

  // Labels
  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useLabels({
    id: MANAGE_ORDERS_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  // Table state management
  const {
    data: ordersData,
    totalCount: ordersTotalCount,
    isLoading,
    isFetching,
    error,
    currentPage,
    pageSize,
    searchQuery,
    debouncedSearch,
    sortField,
    sortOrder,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    onSort,
  } = useOrdersTable({
    enabled: !labelsLoading && !labelsError,
  });

  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  // Show loading state
  if (labelsLoading || isLoading) {
    return (
      <Box testId="manage-orders-page">
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.manageOrders ?? 'Manage Orders'}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.manageOrdersDescription ?? 'Manage and track orders.'}
        </Typography>
        <Box className={styles.loaderContainer}>
          <Loader size={48} theme={theme} />
        </Box>
      </Box>
    );
  }

  // Show error state only when there's no search query
  if (labelsError) {
    return (
      <Box testId="manage-orders-page">
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.errorLoadingLabels ?? 'Error loading labels. Please try again.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error && !searchQuery && !debouncedSearch && !isFetching) {
    return (
      <Box testId="manage-orders-page">
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.manageOrders ?? 'Manage Orders'}
        </Typography>
        <Typography variant="body" size="medium" color="gray-3" theme={theme}>
          {labels?.manageOrdersDescription ?? 'Manage and track orders.'}
        </Typography>
        <Box pt={24}>
          <Typography variant="body" size="large" theme={theme} color="error-1">
            {labels?.errorLoadingOrders ?? 'Error loading orders. Please try again.'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box testId="manage-orders-page">
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {labels?.manageOrders ?? 'Manage Orders'}
      </Typography>
      <Typography variant="body" size="medium" color="gray-3" theme={theme}>
        {labels?.manageOrdersDescription ?? 'Manage and track orders.'}
      </Typography>
      <Box pt={24} className={styles.loadingContainer}>
        {isFetching && (
          <Box className="fetchingOverlay">
            <Loader size={48} theme={invertedTheme} />
          </Box>
        )}
        <Table
          theme={theme}
          data={error ? [] : ordersData}
          totalCount={error ? 0 : ordersTotalCount}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          labels={labels || {}}
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          sortColumn={sortField}
          sortDirection={sortOrder}
          onSort={onSort}
          onOrderSelect={handleOrderSelect}
        />
      </Box>
    </Box>
  );
};

export default ManageOrders;
